/* The button.
 *
 * TWO DIRECTIONS, TWO RULES, ONE COMPONENT
 * The base design system rations `--seal`: a primary button is ink at rest and
 * reaches the accent only on press, so the colour reads as a response rather
 * than as decoration. The plum register does the opposite: the primary action
 * IS the ochre gradient, because that is the dashboard topbar CTA the brand
 * sheet names as the reference.
 *
 * Both are correct for their own surface, so the component holds neither. It
 * reads `--button-*` tokens, and the register decides. The fallbacks below are
 * the base system's behaviour, so this still renders correctly with no
 * register loaded at all.
 *
 * THE PRESS IS translateY, NOT scale
 * The bundle specifies `translateY(1px)` with a reduced shadow over 120ms, and
 * that is what a raised gradient button should do: it sits on the surface, so
 * pressing pushes it down rather than shrinking it. A scale press is right for
 * a flat button and wrong for this one, and the spec is explicit.
 *
 * Minimum target 44px, 56px at the counter. Staff work fast, one-handed, with
 * a queue.
 */
import type { ButtonHTMLAttributes, ReactNode } from "react";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "default" | "counter";

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  children: ReactNode;
};

const SIZES: Record<ButtonSize, React.CSSProperties> = {
  // 12px 22px and 13px/1 are what the artboard computes; the README says
  // 12px 20px, and the artboard is the authority. The min-height is ours:
  // 44px is the smallest target that is not a mis-tap waiting to happen, and
  // the padding alone does not reach it.
  default: { padding: "12px 22px", fontSize: "13px", minHeight: 44 },
  // The till scales these with the tablet it runs on (--counter-btn-*, set in
  // apps/counter/src/styles.ts); anywhere that does not set them gets 56px and
  // 16px, as before.
  counter: {
    padding: "16px 24px",
    fontSize: "var(--counter-btn-fs, 16px)",
    minHeight: "var(--counter-btn-h, 56px)",
  },
};

export function Button({
  variant = "primary",
  size = "default",
  fullWidth = false,
  children,
  style,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      data-variant={variant}
      className={`dg-btn ${rest.className ?? ""}`}
      style={{ ...SIZES[size], width: fullWidth ? "100%" : undefined, ...style }}
    >
      {children}
    </button>
  );
}

/* Styles live next to the component because the design system exports tokens
 * rather than classes, and says so: "the components style themselves; for your
 * own layout glue, use the CSS custom properties below directly." */
export const buttonCss = `
.dg-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid transparent;
  font-family: var(--font-sans);
  font-weight: 600;
  letter-spacing: -0.005em;
  line-height: 1;
  cursor: pointer;
  text-decoration: none;
  /* Named properties only. Never \`all\`: it animates things you did not mean
     to, including layout properties that cost a reflow per frame. */
  transition:
    background var(--duration-fast) var(--ease-out),
    color var(--duration-fast) var(--ease-out),
    border-color var(--duration-fast) var(--ease-out),
    box-shadow 120ms var(--ease-move),
    transform 120ms var(--ease-move);
}
.dg-btn:disabled { opacity: 0.45; cursor: not-allowed; }

/* --- primary ------------------------------------------------------------ */
.dg-btn[data-variant="primary"] {
  background: var(--button-primary-bg, var(--ink));
  color: var(--button-primary-fg, var(--paper-card-raised));
  border-radius: var(--button-primary-radius, var(--radius-md));
  box-shadow: var(--button-primary-shadow, none);
}
.dg-btn[data-variant="primary"]:active:not(:disabled) {
  /* Pushed down, not shrunk. See the note at the top. */
  transform: translateY(1px);
  box-shadow: var(--button-primary-shadow-pressed, none);
}

/* --- secondary ---------------------------------------------------------- */
.dg-btn[data-variant="secondary"] {
  background: var(--button-secondary-bg, transparent);
  color: var(--button-secondary-fg, var(--ink));
  border-color: var(--button-secondary-border, var(--ink));
  border-radius: var(--button-secondary-radius, var(--radius-md));
}
.dg-btn[data-variant="secondary"]:active:not(:disabled) {
  transform: translateY(1px);
  background: var(--paper-card-raised);
}

/* --- ghost and danger --------------------------------------------------- */
.dg-btn[data-variant="ghost"] {
  background: transparent;
  color: var(--ink-soft);
  border-radius: var(--button-secondary-radius, var(--radius-md));
}
.dg-btn[data-variant="ghost"]:active:not(:disabled) {
  transform: translateY(1px);
  background: var(--paper-card);
  color: var(--ink);
}

.dg-btn[data-variant="danger"] {
  background: transparent;
  color: var(--danger);
  border-color: var(--danger);
  border-radius: var(--button-secondary-radius, var(--radius-md));
}
.dg-btn[data-variant="danger"]:active:not(:disabled) {
  transform: translateY(1px);
  background: var(--danger);
  color: var(--paper);
}

/* Hover only where hover is real. On iOS a :hover rule sticks after a tap
   until something else is touched, which leaves the last key pressed looking
   permanently active on a till. */
@media (hover: hover) and (pointer: fine) {
  .dg-btn[data-variant="primary"]:hover:not(:disabled) { filter: brightness(1.06); }
  .dg-btn[data-variant="secondary"]:hover:not(:disabled) { background: var(--paper-card-raised); }
  .dg-btn[data-variant="ghost"]:hover:not(:disabled) { background: var(--paper-card); color: var(--ink); }
  .dg-btn[data-variant="danger"]:hover:not(:disabled) { background: var(--danger); color: var(--paper); }
}

@media (prefers-reduced-motion: reduce) {
  .dg-btn:active:not(:disabled) { transform: none; }
}
`;
